import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, fmtDate, fmtUsd } from '../api';
import { useAuth } from '../auth';
import { Badge, Button, Card, Empty, ErrorBox, Flash, Loading, PageHeader, StateBadge, Table, useAction, useApi } from '../components/ui';
import { NeedsInfoForm } from './NewRequest';
import { DeliverablesCard } from '../components/Deliverables';

interface RequestRow {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string | null;
  createdAt: string;
  slaDueAt: string | null;
  blockedReason: string | null;
  missingFields: { path: string; question: string }[];
}

export function Requests() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const { data, error, loading } = useApi<RequestRow[]>(`/api/requests${status ? `?status=${status}` : ''}`, [status]);
  const rows = (data ?? []).filter((r) => !q || `${r.id} ${r.title}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHeader
        title="Demandas"
        subtitle="Intake (W01): cada demanda recebe ID, briefing, prioridade, tarefa e SLA."
        actions={
          <Link to="/demandas/nova" className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white">
            Nova demanda
          </Link>
        }
      />
      <div className="mb-3 flex flex-wrap gap-2">
        <label className="text-sm">
          <span className="sr-only">Buscar</span>
          <input className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Buscar por ID ou título" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="sr-only">Status</span>
          <select className="rounded-md border border-slate-300 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos os status</option>
            {['NEEDS_INFO', 'BRIEF_READY', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELED'].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? (
        <Loading />
      ) : (
        <Card>
          <Table head={['ID', 'Título', 'Tipo', 'Status', 'Prioridade', 'Criada', 'SLA']} empty={!rows.length}>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono text-xs">
                  <Link className="text-brand-700 underline" to={`/demandas/${r.id}`}>
                    {r.id}
                  </Link>
                </td>
                <td className="px-3 py-2">{r.title}</td>
                <td className="px-3 py-2">{r.type}</td>
                <td className="px-3 py-2">
                  <StateBadge state={r.status} />
                </td>
                <td className="px-3 py-2">{r.priority ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.createdAt)}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.slaDueAt)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<any>(`/api/requests/${id}`, [id]);
  const act = useAction();
  // Atualiza sozinho enquanto a esteira ainda está processando.
  const busy = !!data && (['BRIEF_READY'].includes(data.request.status) || data.assets.some((a: any) => ['BRIEF_READY', 'PRIORITIZED', 'RESEARCHING', 'DRAFTING', 'DESIGNING', 'TECH_REVIEW', 'BRAND_COMPLIANCE_REVIEW'].includes(a.state)));
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
  }, [busy, reload]);
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const r = data.request;
  const brief = data.briefs[0]?.data;
  return (
    <>
      <PageHeader title={`${r.id} · ${r.title}`} subtitle={<span className="flex flex-wrap items-center gap-2"><StateBadge state={r.status} /> Prioridade {r.priority ?? '—'} · SLA {fmtDate(r.slaDueAt)}</span>} actions={<Button variant="secondary" onClick={reload}>Atualizar</Button>} />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      {r.blockedReason && (
        <div role="alert" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {r.blockedReason}
          {!r.duplicateOf && r.blockedReason.startsWith('Falha no processamento') && can('content:edit') && (
            <div className="mt-2">
              <Button variant="secondary" disabled={act.busy} onClick={() => act.run(() => api(`/api/requests/${r.id}/retry`, { method: 'POST' }), 'Demanda reenviada para processamento.').then(reload)}>
                Reprocessar
              </Button>
            </div>
          )}
          {r.duplicateOf && can('content:edit') && (
            <div className="mt-2">
              <Button variant="secondary" disabled={act.busy} onClick={() => act.run(() => api(`/api/requests/${r.id}/confirm-not-duplicate`, { method: 'POST' }), 'Demanda liberada.').then(reload)}>
                Não é duplicidade — continuar
              </Button>
            </div>
          )}
        </div>
      )}
      {r.status === 'NEEDS_INFO' && (
        <Card title="Informações necessárias" className="mb-4">
          <p className="mb-3 text-xs text-slate-600">Nada foi produzido até que estes campos sejam informados.</p>
          <NeedsInfoForm requestId={r.id} missing={r.missingFields} onDone={reload} />
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Briefing (A02)" className="lg:col-span-2">
          {brief ? (
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              {Object.entries({ Objetivo: brief.objective, Persona: brief.persona, Vertical: brief.vertical, Funil: brief.funnelStage, 'Mensagem principal': brief.keyMessage, 'Proposta de valor': brief.valueProposition, CTA: brief.cta, KPI: brief.kpi }).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs font-semibold uppercase text-slate-500">{k}</dt>
                  <dd className={String(v).startsWith('[HIPÓTESE]') ? 'rounded bg-amber-50 px-1 text-amber-900' : ''}>{String(v)}</dd>
                </div>
              ))}
              {brief.constraints?.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase text-slate-500">Restrições</dt>
                  <dd>
                    <ul className="list-disc pl-5">
                      {brief.constraints.map((c: string) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <Empty>Briefing ainda não gerado.</Empty>
          )}
        </Card>
        <Card title="Peças">
          {data.assets.length ? (
            <ul className="space-y-2 text-sm">
              {data.assets.map((a: any) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2">
                  <Link className="font-mono text-brand-700 underline" to={`/conteudo/${a.id}`}>
                    {a.id}
                  </Link>
                  <Badge>{a.channel}</Badge>
                  <StateBadge state={a.state} />
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Nenhuma peça criada.</Empty>
          )}
          <h3 className="mt-4 text-xs font-semibold uppercase text-slate-500">Tarefas</h3>
          <ul className="mt-1 space-y-1 text-xs">
            {data.tasks.map((t: any) => (
              <li key={t.id}>
                {t.title} — {t.status} · quadro: {t.externalSystem} ({t.syncStatus})
              </li>
            ))}
          </ul>
        </Card>
        {data.deliverables && <DeliverablesCard requestId={data.request.id} type={data.request.type} deliverables={data.deliverables} canRun={can('request:read_all')} onDone={reload} />}
        <Card title="Execuções de agentes" className="lg:col-span-3">
          <Table head={['Agente', 'Status', 'Duração', 'Custo', 'Início', 'Erro']} empty={!data.agentRuns.length}>
            {data.agentRuns.map((x: any) => (
              <tr key={x.id}>
                <td className="px-3 py-1">{x.agentId}</td>
                <td className="px-3 py-1">{x.status}</td>
                <td className="px-3 py-1">{x.durationMs ?? '—'} ms</td>
                <td className="px-3 py-1">{fmtUsd(x.costUsd)}</td>
                <td className="px-3 py-1 text-xs">{fmtDate(x.startedAt)}</td>
                <td className="px-3 py-1 text-xs text-red-800">{x.error}</td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Histórico (auditoria)" className="lg:col-span-3">
          <ol className="space-y-1 text-xs">
            {data.timeline.map((e: any) => (
              <li key={e.id} className="font-mono">
                {fmtDate(e.at)} · {e.actorId} · {e.action} · {e.entityId} {e.data?.to ? `→ ${e.data.to}` : ''}
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}
